// ============================================================================
// TABLE LOGIC UTILITIES
// ============================================================================
// Extracted from TrialBalanceTable.tsx for modularity (UI/UX Refinement Phase).
// Contains: filtering, subtotal calculation, and hierarchy building.
//
// CONSTRAINT: All financial math uses integer-cent precision.
// No floating-point operations on financial values.
// ============================================================================

import type { TBAccount, FilterState, FilterMatchType, NumericMatchType } from '../types';
import { dollarsToCents } from './format';
import { getFSRef, FS_REF_LIST } from './mappings';

// ============================================================================
// SHARED TYPES (consumed by TrialBalanceTable.tsx rendering)
// ============================================================================

export interface SubtotalData {
  label: string;
  count: number;
  bookBalance: number;
  rje: number;
  aje: number;
  adjBook: number;
  mAdj: number;
  ftax: number;
  pyAdjBook: number;
  pyMAdj: number;
  pyFtax: number;
}

export type RowType = 'entity' | 'fsref' | 'grouping' | 'subgrouping' | 'account';

export interface TableRow {
  id: string;
  type: RowType;
  level: number;     // Hierarchy depth (0-4)
  indent: number;    // Visual indentation
  isSubtotal: boolean;
  data: SubtotalData | TBAccount;
  accountIds?: string[];  // For subtotals: list of underlying account IDs
  parentId?: string;      // Parent row ID for collapse/expand
}

// Re-export ViewState so the table file can import it from here or keep its own
export interface ViewState {
  showEntities: boolean;
  showFSRef: boolean;
  showGroupings: boolean;
  showSubGroupings: boolean;
  expandAll: boolean;
}

// ============================================================================
// FILTERING LOGIC
// ============================================================================

export function matchesTextFilter(
  value: string,
  filterValue: string,
  matchType: FilterMatchType
): boolean {
  if (!filterValue.trim()) return true;

  const normalizedValue = value.toLowerCase();
  const normalizedFilter = filterValue.toLowerCase().trim();

  switch (matchType) {
    case 'contains':
      return normalizedValue.includes(normalizedFilter);
    case 'equals':
      return normalizedValue === normalizedFilter;
    case 'startsWith':
      return normalizedValue.startsWith(normalizedFilter);
    case 'notContains':
      return !normalizedValue.includes(normalizedFilter);
    case 'notEquals':
      return normalizedValue !== normalizedFilter;
    default:
      return true;
  }
}

export function matchesNumericFilter(
  valueCents: number,
  filterValue: string,
  matchType: NumericMatchType
): boolean {
  if (!filterValue.trim()) return true;

  const filterNum = parseFloat(filterValue);
  if (isNaN(filterNum)) return true;

  // Convert display dollars → integer cents for comparison
  const filterCents = dollarsToCents(filterNum);

  switch (matchType) {
    case 'equals':
      return valueCents === filterCents;
    case 'greaterThan':
      return valueCents > filterCents;
    case 'lessThan':
      return valueCents < filterCents;
    case 'greaterOrEqual':
      return valueCents >= filterCents;
    case 'lessOrEqual':
      return valueCents <= filterCents;
    case 'notEquals':
      return valueCents !== filterCents;
    default:
      return true;
  }
}

export function filterAccounts(
  accounts: TBAccount[],
  filters: FilterState
): TBAccount[] {
  return accounts.filter(account => {
    if (filters.accountNumber.value && !matchesTextFilter(
      account.accountNumber,
      filters.accountNumber.value,
      filters.accountNumber.matchType as FilterMatchType
    )) return false;

    if (filters.accountName.value && !matchesTextFilter(
      account.accountName,
      filters.accountName.value,
      filters.accountName.matchType as FilterMatchType
    )) return false;

    if (filters.entity.value && !matchesTextFilter(
      account.entity,
      filters.entity.value,
      filters.entity.matchType as FilterMatchType
    )) return false;

    if (filters.grouping.value && !matchesTextFilter(
      account.grouping,
      filters.grouping.value,
      filters.grouping.matchType as FilterMatchType
    )) return false;

    if (filters.subGrouping.value && !matchesTextFilter(
      account.subGrouping || '',
      filters.subGrouping.value,
      filters.subGrouping.matchType as FilterMatchType
    )) return false;

    if (filters.bookBalance.value && !matchesNumericFilter(
      account.bookBalance,
      filters.bookBalance.value,
      filters.bookBalance.matchType as NumericMatchType
    )) return false;

    if (filters.rje.value && !matchesNumericFilter(
      account.rje,
      filters.rje.value,
      filters.rje.matchType as NumericMatchType
    )) return false;

    if (filters.aje.value && !matchesNumericFilter(
      account.aje,
      filters.aje.value,
      filters.aje.matchType as NumericMatchType
    )) return false;

    if (filters.adjBook.value && !matchesNumericFilter(
      account.adjBook ?? 0,
      filters.adjBook.value,
      filters.adjBook.matchType as NumericMatchType
    )) return false;

    if (filters.mAdj.value && !matchesNumericFilter(
      account.mAdj,
      filters.mAdj.value,
      filters.mAdj.matchType as NumericMatchType
    )) return false;

    if (filters.ftax.value && !matchesNumericFilter(
      account.ftax ?? 0,
      filters.ftax.value,
      filters.ftax.matchType as NumericMatchType
    )) return false;

    return true;
  });
}

// ============================================================================
// HIERARCHY BUILDER
// ============================================================================

export function calculateSubtotal(accounts: TBAccount[], label: string): SubtotalData {
  return {
    label,
    count: accounts.length,
    bookBalance: accounts.reduce((sum, acc) => sum + acc.bookBalance, 0),
    rje: accounts.reduce((sum, acc) => sum + acc.rje, 0),
    aje: accounts.reduce((sum, acc) => sum + acc.aje, 0),
    adjBook: accounts.reduce((sum, acc) => sum + (acc.adjBook ?? 0), 0),
    mAdj: accounts.reduce((sum, acc) => sum + acc.mAdj, 0),
    ftax: accounts.reduce((sum, acc) => sum + (acc.ftax ?? 0), 0),
    pyAdjBook: accounts.reduce((sum, acc) => sum + acc.pyAdjBook, 0),
    pyMAdj: accounts.reduce((sum, acc) => sum + acc.pyMAdj, 0),
    pyFtax: accounts.reduce((sum, acc) => sum + acc.pyFtax, 0),
  };
}

export function buildHierarchy(accounts: TBAccount[], viewState: ViewState): TableRow[] {
  const rows: TableRow[] = [];

  const showEntity = viewState.showEntities;
  const showFSRef = viewState.showFSRef;
  const showGrouping = viewState.showGroupings;
  const showSubGrouping = viewState.showSubGroupings;

  if (!showEntity && !showFSRef && !showGrouping && !showSubGrouping) {
    const sortedAccounts = [...accounts].sort((a, b) =>
      a.accountNumber.localeCompare(b.accountNumber)
    );

    sortedAccounts.forEach(acc => {
      rows.push({
        id: acc.id!,
        type: 'account',
        level: 0,
        indent: 0,
        isSubtotal: false,
        data: acc,
        parentId: undefined
      });
    });

    return rows;
  }

  // Group by Entity first
  const byEntity = new Map<string, TBAccount[]>();
  accounts.forEach(acc => {
    const entity = acc.entity || 'Default Entity';
    if (!byEntity.has(entity)) {
      byEntity.set(entity, []);
    }
    byEntity.get(entity)!.push(acc);
  });

  const sortedEntities = Array.from(byEntity.keys()).sort();

  sortedEntities.forEach(entity => {
    const entityAccounts = byEntity.get(entity)!;
    const entityId = `entity-${entity}`;
    let currentParentId: string | undefined = undefined;
    let currentIndent = 0;

    if (showEntity) {
      rows.push({
        id: entityId,
        type: 'entity',
        level: 0,
        indent: 0,
        isSubtotal: true,
        data: calculateSubtotal(entityAccounts, entity),
        accountIds: entityAccounts.map(a => a.id!)
      });
      currentParentId = entityId;
      currentIndent = 1;
    }

    const byFSRef = new Map<string, TBAccount[]>();
    entityAccounts.forEach(acc => {
      const fsRef = getFSRef(acc.grouping);
      if (!byFSRef.has(fsRef)) {
        byFSRef.set(fsRef, []);
      }
      byFSRef.get(fsRef)!.push(acc);
    });

    const sortedFSRefs = FS_REF_LIST.filter(fsRef => byFSRef.has(fsRef));

    sortedFSRefs.forEach(fsRef => {
      const fsRefAccounts = byFSRef.get(fsRef)!;
      const fsRefId = `fsref-${entity}-${fsRef}`;
      let fsRefParentId = currentParentId;
      let fsRefIndent = currentIndent;

      if (showFSRef) {
        rows.push({
          id: fsRefId,
          type: 'fsref',
          level: 1,
          indent: fsRefIndent,
          isSubtotal: true,
          data: calculateSubtotal(fsRefAccounts, fsRef),
          accountIds: fsRefAccounts.map(a => a.id!),
          parentId: currentParentId
        });
        fsRefParentId = fsRefId;
        fsRefIndent = currentIndent + 1;
      }

      const byGrouping = new Map<string, TBAccount[]>();
      fsRefAccounts.forEach(acc => {
        const grouping = acc.grouping || 'Ungrouped';
        if (!byGrouping.has(grouping)) {
          byGrouping.set(grouping, []);
        }
        byGrouping.get(grouping)!.push(acc);
      });

      const sortedGroupings = Array.from(byGrouping.keys()).sort();

      sortedGroupings.forEach(grouping => {
        const groupingAccounts = byGrouping.get(grouping)!;
        const groupingId = `grouping-${entity}-${fsRef}-${grouping}`;
        let groupingIndent = fsRefIndent;

        if (showGrouping) {
          rows.push({
            id: groupingId,
            type: 'grouping',
            level: 2,
            indent: groupingIndent,
            isSubtotal: true,
            data: calculateSubtotal(groupingAccounts, grouping),
            accountIds: groupingAccounts.map(a => a.id!),
            parentId: fsRefParentId
          });
          
          groupingIndent = fsRefIndent + 1;
        }

        // Effective parent for items at the account/subgrouping level:
        // if Grouping row was pushed, use it; otherwise float up to fsRef/entity parent.
        const effectiveGroupingParentId = showGrouping ? groupingId : fsRefParentId;

        if (showSubGrouping) {
          const bySubGrouping = new Map<string, TBAccount[]>();
          groupingAccounts.forEach(acc => {
            const subGrouping = acc.subGrouping || '';
            if (!bySubGrouping.has(subGrouping)) {
              bySubGrouping.set(subGrouping, []);
            }
            bySubGrouping.get(subGrouping)!.push(acc);
          });

          const sortedSubGroupings = Array.from(bySubGrouping.keys()).sort((a, b) => {
            if (a === '' && b !== '') return -1;
            if (a !== '' && b === '') return 1;
            return a.localeCompare(b);
          });

          sortedSubGroupings.forEach(subGrouping => {
            const subGroupingAccounts = bySubGrouping.get(subGrouping)!;

            if (subGrouping) {
              const subGroupingId = `subgrouping-${entity}-${fsRef}-${grouping}-${subGrouping}`;

              rows.push({
                id: subGroupingId,
                type: 'subgrouping',
                level: 3,
                indent: groupingIndent,
                isSubtotal: true,
                data: calculateSubtotal(subGroupingAccounts, subGrouping),
                accountIds: subGroupingAccounts.map(a => a.id!),
                parentId: effectiveGroupingParentId
              });

              subGroupingAccounts.forEach(acc => {
                rows.push({
                  id: acc.id!,
                  type: 'account',
                  level: 4,
                  indent: groupingIndent + 1,
                  isSubtotal: false,
                  data: acc,
                  parentId: subGroupingId
                });
              });
            } else {
              // Accounts with no subgrouping float up to nearest active parent
              subGroupingAccounts.forEach(acc => {
                rows.push({
                  id: acc.id!,
                  type: 'account',
                  level: 4,
                  indent: groupingIndent,
                  isSubtotal: false,
                  data: acc,
                  parentId: effectiveGroupingParentId
                });
              });
            }
          });
        } else {
          // SubGroupings OFF: attach accounts directly to nearest active parent
          const sortedAccounts = [...groupingAccounts].sort((a, b) =>
            a.accountNumber.localeCompare(b.accountNumber)
          );
          sortedAccounts.forEach(acc => {
            rows.push({
              id: acc.id!,
              type: 'account',
              level: 4,
              indent: groupingIndent,
              isSubtotal: false,
              data: acc,
              parentId: effectiveGroupingParentId
            });
          });
        }
      });
    });
  });

  return rows;
}
